import { Deployment, DeploymentStatus, GpuSelection } from '@/types/deployment'
import { DeploymentOverview } from '@/types/deploymentGroup'
import { Device, Gpu } from '@/types/device'
import {
  getDeviceGpus,
  isGpuReservedForVlm,
  isGpuUnreadable,
  isJetsonDevice
} from '@/utils/device/getDeviceAttr'

export type GpuSelectionError = 'nothingSelected' | 'allReserved' | 'noGpuInfo'

export type SavedVlmGpus = Record<string, number[] | undefined>

export type RunningGpus = Record<string, number | undefined>

export type HeldGpuMemory = Record<string, Record<number, number>>

const RELEASED_STATUSES = [
  DeploymentStatus.Inactive,
  DeploymentStatus.Failed,
  DeploymentStatus.Removing,
  DeploymentStatus.Stopping,
  DeploymentStatus.Stopped
]

export const getReadableGpus = (device: Device) =>
  getDeviceGpus(device).filter((gpu) => !isGpuUnreadable(gpu))

export const getOfferedGpus = (device: Device, savedVlmGpus?: number[]) =>
  isJetsonDevice(device)
    ? []
    : getReadableGpus(device).filter(
        (gpu) => !isGpuReservedForVlm(device, gpu, savedVlmGpus)
      )

export const defaultGpu = (gpus: Gpu[]): Gpu | undefined =>
  gpus.reduce<Gpu | undefined>((best, gpu) => {
    if (!best) {
      return gpu
    }

    const bestMemory = best.memory_total_mb ?? 0
    const memory = gpu.memory_total_mb ?? 0

    return memory > bestMemory ||
      (memory === bestMemory && gpu.index < best.index)
      ? gpu
      : best
  }, undefined)

// One GPU from a saved choice: the GPU the groups run on while it is still
// offered, else the best offered GPU of the choice, else the best offered GPU.
export const pickGpu = (
  saved: number[],
  offeredGpus: Gpu[],
  running?: number
): number[] => {
  if (saved.length === 0) {
    return []
  }

  const candidates = offeredGpus.filter((gpu) => saved.includes(gpu.index))

  if (
    running !== undefined &&
    candidates.some((gpu) => gpu.index === running)
  ) {
    return [running]
  }

  const pick = defaultGpu(candidates.length > 0 ? candidates : offeredGpus)

  return pick ? [pick.index] : []
}

export const sanitizeGpuSelection = (
  selection: GpuSelection,
  devices: Device[],
  savedVlm: SavedVlmGpus = {},
  running: RunningGpus = {}
): GpuSelection =>
  Object.fromEntries(
    devices.flatMap((device) => {
      const offeredGpus = getOfferedGpus(device, savedVlm[device.id])
      const current = selection[device.id]

      if (offeredGpus.length === 0) {
        return []
      }

      return [
        [
          device.id,
          current
            ? pickGpu(current, offeredGpus, running[device.id])
            : [defaultGpu(offeredGpus)!.index]
        ]
      ]
    })
  )

export const pruneGpuSelection = (
  selection: GpuSelection,
  deviceIds: string[]
): GpuSelection =>
  Object.fromEntries(
    Object.entries(selection).filter(([deviceId]) =>
      deviceIds.includes(deviceId)
    )
  )

export const getGpuSelectionError = (
  selection: GpuSelection,
  device: Device,
  savedVlmGpus?: number[]
): GpuSelectionError | null => {
  if (isJetsonDevice(device)) {
    return null
  }

  if (getReadableGpus(device).length === 0) {
    return 'noGpuInfo'
  }

  if (getOfferedGpus(device, savedVlmGpus).length === 0) {
    return 'allReserved'
  }

  return selection[device.id]?.length ? null : 'nothingSelected'
}

export const hasGpuSelectionErrors = (
  selection: GpuSelection,
  devices: Device[],
  savedVlm: SavedVlmGpus = {}
) =>
  devices.some(
    (device) =>
      getGpuSelectionError(selection, device, savedVlm[device.id]) !== null
  )

export type GpuSelectionState = 'loading' | 'loadFailed' | 'required' | null

export const getGpuSelectionState = (
  selection: GpuSelection,
  devices: Device[] | undefined,
  loadFailed: boolean,
  savedVlm: SavedVlmGpus = {}
): GpuSelectionState => {
  if (loadFailed) {
    return 'loadFailed'
  }

  if (!devices) {
    return 'loading'
  }

  return hasGpuSelectionErrors(selection, devices, savedVlm) ? 'required' : null
}

export const getInitialGpuSelection = (
  deployments: Pick<Deployment, 'settings_payload' | 'allowed_gpus'>[]
): GpuSelection =>
  deployments.reduce<GpuSelection>((selection, deployment) => {
    const deviceId = deployment.settings_payload?.device_id

    if (!deviceId || selection[deviceId] || !deployment.allowed_gpus?.length) {
      return selection
    }

    return { ...selection, [deviceId]: deployment.allowed_gpus }
  }, {})

export const getRunningGpus = (
  deployments: Pick<Deployment, 'settings_payload' | 'allowed_gpus' | 'gpu'>[]
): RunningGpus =>
  deployments.reduce<RunningGpus>((running, deployment) => {
    const deviceId = deployment.settings_payload?.device_id
    const index = deployment.gpu?.index

    if (
      !deviceId ||
      running[deviceId] !== undefined ||
      index === undefined ||
      !deployment.allowed_gpus?.includes(index)
    ) {
      return running
    }

    return { ...running, [deviceId]: index }
  }, {})

export const getHeldGpuMemory = (
  overview: DeploymentOverview | undefined,
  applicationId: string | undefined
): HeldGpuMemory => {
  const held: HeldGpuMemory = {}

  overview?.results
    .filter((app) => app.application_id === applicationId)
    .flatMap((app) => app.devices)
    .flatMap((device) => device.groups)
    .forEach((group) => {
      const index = group.gpu?.index
      const memory = group.metrics?.gpu_load?.group_memory_mb

      if (
        index === undefined ||
        !memory ||
        RELEASED_STATUSES.includes(group.status)
      ) {
        return
      }

      held[group.device_id] = {
        ...held[group.device_id],
        [index]: (held[group.device_id]?.[index] ?? 0) + memory
      }
    })

  return held
}
